// QRPreviewPanel.jsx
import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import styled from '@emotion/styled';
import QRCodeStyling from 'qr-code-styling';
import { getTemplateConfig } from '../utils/templateConfig';

const PreviewPanel = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 1.5rem;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    margin-bottom: 2rem;
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;

const PreviewHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const PreviewTitle = styled.h3`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const PreviewSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const QRContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  width: 100%;
  aspect-ratio: 1/1;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.25rem;
    max-width: 280px;
  }
`;

const QRPlaceholder = styled.div`
  width: 200px;
  height: 200px;
  border: 2px dashed #e5e7eb;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.875rem;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const QRCodeContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 220px;
  max-height: 220px;
  position: relative;
  z-index: 2;
  margin: 0 auto;

  canvas,
  svg {
    width: 100% !important;
    height: 100% !important;
    max-width: 100%;
    max-height: 100%;
    border-radius: ${({ cornerRadius }) => cornerRadius || 0}px;
  }

  @media (max-width: 768px) {
    max-width: 200px;
    max-height: 200px;
  }

  @media (max-width: 480px) {
    max-width: 180px;
    max-height: 180px;
  }
`;

const QRWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const DetailsToggle = styled.button`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ToggleIcon = styled.span`
  font-size: 0.75rem;
  transition: transform 0.2s ease;
  transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0deg')});
`;

const DetailsDropdown = styled.div`
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  max-height: ${({ expanded }) => (expanded ? '400px' : '0px')};
  opacity: ${({ expanded }) => (expanded ? '1' : '0')};
  margin-top: ${({ expanded }) => (expanded ? '1rem' : '0')};
`;

const DetailsContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem;
  backdrop-filter: blur(10px);
`;

const DetailSection = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.08);
  padding: 0.375rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const QualityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${({ quality }) => {
    switch (quality) {
      case 'excellent':
        return 'rgba(34, 197, 94, 0.15)';
      case 'good':
        return 'rgba(59, 130, 246, 0.15)';
      case 'warning':
        return 'rgba(245, 158, 11, 0.15)';
      case 'poor':
        return 'rgba(239, 68, 68, 0.15)';
      default:
        return 'rgba(156, 163, 175, 0.15)';
    }
  }};
  border: 1px solid
    ${({ quality }) => {
      switch (quality) {
        case 'excellent':
          return 'rgba(34, 197, 94, 0.3)';
        case 'good':
          return 'rgba(59, 130, 246, 0.3)';
        case 'warning':
          return 'rgba(245, 158, 11, 0.3)';
        case 'poor':
          return 'rgba(239, 68, 68, 0.3)';
        default:
          return 'rgba(156, 163, 175, 0.3)';
      }
    }};
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ quality }) => {
    switch (quality) {
      case 'excellent':
        return '#22c55e';
      case 'good':
        return '#3b82f6';
      case 'warning':
        return '#f59e0b';
      case 'poor':
        return '#ef4444';
      default:
        return '#9ca3af';
    }
  }};
`;

const DownloadButton = styled.button`
  background: linear-gradient(
    135deg,
    rgba(124, 58, 237, 0.12),
    rgba(236, 72, 153, 0.12)
  );
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.8rem 1.5rem;
  width: 100%;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(124, 58, 237, 0.18),
      rgba(236, 72, 153, 0.18)
    );
    transform: translateY(-1px);
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transition: left 0.7s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const ContentPreview = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  font-family:
    'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New',
    monospace;
  line-height: 1.4;
  word-break: break-all;
  max-height: 100px;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const QRPreviewPanel = forwardRef(
  ({ formData, contentType, qrOptions, canProceed, onDownloadClick }, ref) => {
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const qrRef = useRef(null);
    const qrCodeInstance = useRef(null);

    // Expose QR instance to parent components
    useImperativeHandle(ref, () => ({
      getQRInstance: () => qrCodeInstance.current,
      regenerateQR: () => generateQRCode(),
    }));

    const getContentData = () => {
      switch (contentType) {
        case 'url':
          return formData.url;
        case 'text':
          return formData.text;
        case 'contact': {
          const { firstName, lastName, email, phone, organization } =
            formData.contact;

          // Generate vCard format for contact
          if (firstName || lastName || email || phone) {
            return `BEGIN:VCARD
VERSION:3.0
FN:${firstName} ${lastName}
N:${lastName};${firstName};;;
ORG:${organization || ''}
EMAIL:${email}
TEL:${phone}
END:VCARD`;
          }
          return '';
        }
        default:
          return '';
      }
    };

    const getContentType = () => {
      switch (contentType) {
        case 'url':
          return 'Website URL';
        case 'text':
          return 'Plain Text';
        case 'contact':
          return 'Contact vCard';
        default:
          return '';
      }
    };

    const getQualityAssessment = () => {
      const contentLength = getContentData().length;
      const hasLogo = !!qrOptions.logoFile;
      const errorLevel = qrOptions.errorCorrectionLevel;

      // Base quality assessment
      let quality = 'excellent';
      let message = 'Optimal scanning quality';

      // Check content length
      if (contentLength > 2000) {
        quality = 'warning';
        message = 'Large content may affect scanning';
      } else if (contentLength > 4000) {
        quality = 'poor';
        message = 'Content too large, scanning may fail';
      }

      // Check logo size vs error correction
      if (hasLogo) {
        const logoSizePercent = qrOptions.logoSize;
        const maxSafeSize = { L: 7, M: 15, Q: 25, H: 35 }[errorLevel];

        if (logoSizePercent > maxSafeSize) {
          quality = 'poor';
          message = 'Logo too large for error correction level';
        } else if (logoSizePercent > maxSafeSize * 0.8) {
          quality = 'warning';
          message = 'Logo size near maximum for reliability';
        } else if (quality === 'excellent') {
          quality = 'good';
          message = 'Good quality with logo';
        }
      }

      return { quality, message };
    };

    const generateQRCode = useCallback(() => {
      const buildQRConfig = () => {
        const baseConfig = {
          width: 512,
          height: 512,
          margin: qrOptions.includeMargin ? 4 : 0,
          qrOptions: {
            errorCorrectionLevel: qrOptions.errorCorrectionLevel,
          },
          backgroundOptions: {
            color: qrOptions.backgroundColor,
          },
        };

        // Merge template-specific configuration with base config
        const templateConfig = getTemplateConfig(
          qrOptions.template,
          qrOptions.foregroundColor,
        );

        return { ...baseConfig, ...templateConfig };
      };

      const getContentDataInternal = () => {
        switch (contentType) {
          case 'url':
            return formData.url;
          case 'text':
            return formData.text;
          case 'contact': {
            const { firstName, lastName, email, phone, organization } =
              formData.contact;

            // Generate vCard format for contact
            if (firstName || lastName || email || phone) {
              return `BEGIN:VCARD
VERSION:3.0
FN:${firstName} ${lastName}
N:${lastName};${firstName};;;
ORG:${organization || ''}
EMAIL:${email}
TEL:${phone}
END:VCARD`;
            }
            return '';
          }
          default:
            return '';
        }
      };

      const content = getContentDataInternal();

      if (!qrRef.current || !content) return;

      const config = {
        ...buildQRConfig(),
        data: content,
      };

      // Add logo if present with safe sizing for scannability
      if (qrOptions.logoFile) {
        const maxLogoSizes = {
          L: 0.07, // 7%
          M: 0.15, // 15%
          Q: 0.25, // 25%
          H: 0.35, // 35%
        };

        // Use high error correction when logo is present
        const errorLevel = 'H';
        const userSizeRatio = qrOptions.logoSize / 100;
        const logoSizeRatio = Math.min(userSizeRatio, maxLogoSizes[errorLevel]);

        config.image = qrOptions.logoFile;
        config.imageOptions = {
          hideBackgroundDots: true,
          imageSize: logoSizeRatio,
          margin: 2,
          crossOrigin: 'anonymous',
          saveAsBlob: true,
        };

        // Force high error correction for logo QR codes
        config.qrOptions.errorCorrectionLevel = 'H';
      }

      // Create new QR code instance
      qrCodeInstance.current = new QRCodeStyling(config);

      // Clear previous QR code
      qrRef.current.innerHTML = '';

      // Append to DOM
      qrCodeInstance.current.append(qrRef.current);
    }, [formData, contentType, qrOptions]);

    useEffect(() => {
      if (canProceed) {
        generateQRCode();
      }
    }, [canProceed, generateQRCode]);

    const contentData = getContentData();
    const hasContent = canProceed && contentData;
    const qualityInfo = hasContent ? getQualityAssessment() : null;

    return (
      <PreviewPanel>
        <PreviewHeader>
          <PreviewTitle>Live Preview</PreviewTitle>
          <PreviewSubtitle>Your QR code updates in real-time</PreviewSubtitle>
        </PreviewHeader>

        <QRContainer hasContent={hasContent}>
          {hasContent ? (
            <QRWrapper>
              <QRCodeContainer
                ref={qrRef}
                cornerRadius={qrOptions.cornerRadius}
              />
            </QRWrapper>
          ) : (
            <QRPlaceholder>
              Enter content to see
              <br />
              your QR code
            </QRPlaceholder>
          )}
        </QRContainer>

        {hasContent && (
          <DownloadButton onClick={onDownloadClick}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 16L7 11H10V4H14V11H17L12 16Z" fill="currentColor" />
              <path d="M5 20V18H19V20H5Z" fill="currentColor" />
            </svg>
            Download QR Code
          </DownloadButton>
        )}

        {hasContent && (
          <>
            <DetailsToggle onClick={() => setDetailsExpanded(!detailsExpanded)}>
              <span>QR Code Details</span>
              <ToggleIcon expanded={detailsExpanded}>▼</ToggleIcon>
            </DetailsToggle>

            <DetailsDropdown expanded={detailsExpanded}>
              <DetailsContent>
                <DetailSection>
                  <SectionTitle>Quality Assessment</SectionTitle>
                  {qualityInfo && (
                    <QualityBadge quality={qualityInfo.quality}>
                      <span>
                        {qualityInfo.quality === 'excellent' && '✓'}
                        {qualityInfo.quality === 'good' && '✓'}
                        {qualityInfo.quality === 'warning' && '⚠'}
                        {qualityInfo.quality === 'poor' && '⚠'}
                      </span>
                      {qualityInfo.message}
                    </QualityBadge>
                  )}
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Configuration</SectionTitle>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Content Type</InfoLabel>
                      <InfoValue>{getContentType()}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Template</InfoLabel>
                      <InfoValue>
                        {qrOptions.template.charAt(0).toUpperCase() +
                          qrOptions.template.slice(1)}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Error Correction</InfoLabel>
                      <InfoValue>{qrOptions.errorCorrectionLevel}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Output Size</InfoLabel>
                      <InfoValue>512×512 px</InfoValue>
                    </InfoItem>
                    {qrOptions.logoFile && (
                      <>
                        <InfoItem>
                          <InfoLabel>Logo Size</InfoLabel>
                          <InfoValue>{qrOptions.logoSize}%</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Logo Present</InfoLabel>
                          <InfoValue>Yes</InfoValue>
                        </InfoItem>
                      </>
                    )}
                    <InfoItem>
                      <InfoLabel>Data Size</InfoLabel>
                      <InfoValue>{contentData.length} characters</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Margin</InfoLabel>
                      <InfoValue>
                        {qrOptions.includeMargin ? 'Included' : 'None'}
                      </InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Content Preview</SectionTitle>
                  <ContentPreview>
                    {contentData.length > 100
                      ? `${contentData.substring(0, 100)}...`
                      : contentData}
                  </ContentPreview>
                </DetailSection>
              </DetailsContent>
            </DetailsDropdown>
          </>
        )}
      </PreviewPanel>
    );
  },
);

QRPreviewPanel.displayName = 'QRPreviewPanel';

export default QRPreviewPanel;
